import { trackDesignSystemAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { Loader } from 'components/Widget/Loader';
import { DesignSystem, SceneDsLink } from 'dragonmouse-shared/interfaces';
import useDsLinkState, { DsLinkState } from 'hooks/useDsLinkState';
import { useEffect, useMemo, useRef, useState } from 'react';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { shallowEqual, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import { useTheme } from 'styled-components';
import LinkPanel from './LinkPanel';
import ActiveDsPanel from './ActiveDsPanel';
import * as S from './styles';

interface DsPickerProps {
	selection?: SceneDsLink | null;
	designSystems: DesignSystem[];
	onSelect: (designSystem: DesignSystem | null) => Promise<void>;
	onCreateNew: () => void;
	onUpgrade?: () => Promise<void>;
	onBrowseShowcase?: () => void;
	isExternalLoading?: boolean;
	externalLoadingLabel?: string;
}

const DsPicker = ({
	selection,
	designSystems,
	onSelect,
	onCreateNew,
	onUpgrade,
	onBrowseShowcase,
	isExternalLoading = false,
	externalLoadingLabel,
}: DsPickerProps) => {
	const theme = useTheme();
	const dsLinkState = useDsLinkState();
	const { dsModes, chatDesignSystemMode } = useSelector(
		(r: RootReducer) => ({
			dsModes: r.db.activeSceneDesignSystemModes,
			chatDesignSystemMode: r.mode.chatDesignSystemMode,
		}),
		shallowEqual
	);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [showLinkPanel, setShowLinkPanel] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [tooltipId] = useState(() => `ds-picker-tooltip-${Math.random()}`);
	const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below' | 'center'>('above');

	const hasActiveDs = !!selection;
	const activeDesignSystem = selection?.designSystem ?? null;

	// Derive active mode color for swatch indicator
	const hasModes = dsModes && dsModes.length >= 2;
	const activeModeColor = useMemo(() => {
		if (!hasModes) return null;
		const defaultModeId = dsModes.find((t) => t.default)?.id ?? dsModes[0]?.id;
		const activeId = chatDesignSystemMode ?? defaultModeId;
		const activeMode = dsModes.find((t) => t.id === activeId);
		return activeMode?.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT;
	}, [dsModes, chatDesignSystemMode, hasModes]);

	// Close dropdown when clicking outside
	useOnClickOutside(dropdownRef, () => {
		if (isDropdownOpen) {
			setIsDropdownOpen(false);
			setShowLinkPanel(false);
		}
	});

	// Close dropdown when pressing Escape
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isDropdownOpen) {
				setIsDropdownOpen(false);
				setShowLinkPanel(false);
				// Remove focus from button to prevent outline
				if (buttonRef.current) {
					buttonRef.current.blur();
				}
			}
		};

		window.addEventListener('keydown', handleEscape);
		return () => {
			window.removeEventListener('keydown', handleEscape);
		};
	}, [isDropdownOpen]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const calculateDropdownPosition = () => {
		if (!buttonRef.current) return 'above';

		const buttonRect = buttonRef.current.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const dropdownHeight = 454; // Approximate height of dropdown (can be adjusted)
		const gap = 8; // Gap between button and dropdown

		const spaceAbove = buttonRect.top;
		const spaceBelow = viewportHeight - buttonRect.bottom;

		// If we can fit above without overflowing, prefer above
		if (spaceAbove >= dropdownHeight + gap) {
			return 'above';
		}
		// If we can fit below without overflowing, use below
		if (spaceBelow >= dropdownHeight + gap) {
			return 'below';
		}
		// If neither fits well, center it (split overflow)
		return 'center';
	};

	const onToggleDropdown = () => {
		// When no DSs exist and showcase is available, skip the dropdown and open showcase directly
		if (!hasActiveDs && designSystems.length === 0 && onBrowseShowcase) {
			trackDesignSystemAction('click_add_ds_shortcut_gallery');
			onBrowseShowcase();
			return;
		}

		const newIsOpen = !isDropdownOpen;
		if (newIsOpen) {
			// Calculate position when opening
			setDropdownPosition(calculateDropdownPosition());
		}
		setIsDropdownOpen(newIsOpen);
		// Reset link panel visibility when closing dropdown
		if (!newIsOpen) {
			setShowLinkPanel(false);
		}
	};

	const handleSelect = async (designSystem: DesignSystem | null) => {
		// Close dropdown immediately for better UX
		setIsDropdownOpen(false);
		setShowLinkPanel(false);
		setIsLoading(true);

		try {
			await onSelect(designSystem);
		} finally {
			setIsLoading(false);
		}
	};

	// Determine when to show tooltip:
	// 1. Empty state: Show tooltip to guide user to add first design system
	// 2. Active DS + compact mode: Show design system title since text is hidden
	// 3. Active DS + full mode: Show hint to manage design system
	const showLoading = isLoading || isExternalLoading;

	const shouldShowTooltip = !showLoading;
	const tooltipContent = !hasActiveDs
		? 'Click to add first design system'
		: 'Click to manage design system';

	return (
		<S.DropdownContainer ref={dropdownRef}>
			<S.DsControl
				ref={buttonRef}
				isEmpty={!hasActiveDs}
				isActive={hasActiveDs}
				isLoading={dsLinkState === DsLinkState.PUBLISHING || showLoading}
				onClick={onToggleDropdown}
				data-tooltip-id={shouldShowTooltip ? tooltipId : undefined}
				data-tooltip-content={tooltipContent}
			>
				{showLoading ? (
					<>
						<Loader width={12} height={12} />
						<S.DsText>{externalLoadingLabel || 'updating...'}</S.DsText>
					</>
				) : hasActiveDs ? (
					<>
						{dsLinkState === DsLinkState.FRESH || dsLinkState === DsLinkState.DRAFT_EXISTS ? (
							<S.DsStatusContainer>
								{activeModeColor ? (
									<S.ModeSwatch $color={activeModeColor} />
								) : (
									<Icon type={IconType.STACK} width={14} height={14} />
								)}
							</S.DsStatusContainer>
						) : null}
						{dsLinkState === DsLinkState.PUBLISHING && <Loader width={12} height={12} />}
						{dsLinkState === DsLinkState.STALE && (
							<S.DsStatusContainer>
								<Icon type={IconType.REFRESH} width={12} height={12} color={theme.blue[500]} />
							</S.DsStatusContainer>
						)}
						<S.DsText>{activeDesignSystem?.title || 'Design System'}</S.DsText>
						<S.DsChevron>
							<Icon
								type={IconType.FILLED_ARROW_DOWN}
								width={10}
								height={10}
								color={theme.gray[600]}
							/>
						</S.DsChevron>
					</>
				) : (
					<>
						<S.DsIcon>
							<Icon type={IconType.STACK} width={14} height={14} />
						</S.DsIcon>
						<S.DsText>Add DS</S.DsText>
					</>
				)}
			</S.DsControl>
			{shouldShowTooltip && <Tooltip id={tooltipId} placement="top" />}
			<S.Dropdown isOpen={isDropdownOpen && !showLoading} position={dropdownPosition}>
				{hasActiveDs && !showLinkPanel && selection ? (
					<ActiveDsPanel
						selection={selection}
						onUpgrade={onUpgrade}
						onClose={() => setIsDropdownOpen(false)}
						onShowLinkPanel={() => setShowLinkPanel(true)}
					/>
				) : (
					<LinkPanel
						designSystems={designSystems}
						hasActiveDs={hasActiveDs}
						onCreateNew={onCreateNew}
						onLinkDs={handleSelect}
						onBrowseShowcase={
							onBrowseShowcase
								? () => {
										setIsDropdownOpen(false);
										setShowLinkPanel(false);
										onBrowseShowcase();
								  }
								: undefined
						}
					/>
				)}
			</S.Dropdown>
		</S.DropdownContainer>
	);
};

export default DsPicker;
